type MembershipAlertRow = {
  paymentState?: null | string;
  endDate: string;
  status: string;
};

export type ClientMembershipAlert = 'expired' | 'unpaid' | null;

const LIVE_STATUSES = new Set(['ACTIVE', 'FROZEN', 'PENDING']);

/**
 * What a client has to be told about their own memberships in an organization.
 *
 * `unpaid` — a cycle the client has not paid: still waiting for its card, or
 * running with nothing (`UNPAID`) or only part (`PARTIAL`) of it settled.
 * `paymentState` is `null` on a card-managed row (the card settles it) and
 * `UNKNOWN` when the server could not state the money exactly — neither is
 * called a debt. It is also absent for a reader without money rights.
 *
 * `expired` — nothing is running or about to start, and the most recent
 * cycle simply ran out. A CANCELED or ARCHIVED one ended on purpose and says
 * nothing; a live successor (a renewal) means there is nothing to warn about.
 *
 * Unpaid outranks expired: it is the one that costs the client something now.
 */
export const resolveClientMembershipAlert = (
  rows: MembershipAlertRow[],
): ClientMembershipAlert => {
  const isUnpaid = (row: MembershipAlertRow) =>
    row.status === 'PENDING_PAYMENT_METHOD' ||
    ((row.status === 'ACTIVE' || row.status === 'FROZEN') &&
      (row.paymentState === 'UNPAID' || row.paymentState === 'PARTIAL'));
  if (rows.some(isUnpaid)) return 'unpaid';

  if (rows.some((row) => LIVE_STATUSES.has(row.status))) return null;

  // civil `yyyy-MM-dd` strings compare lexicographically
  const latest = rows.reduce<MembershipAlertRow | null>(
    (best, row) => (!best || row.endDate > best.endDate ? row : best),
    null,
  );
  return latest?.status === 'EXPIRED' ? 'expired' : null;
};
